import { faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './Style.css';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ModalTrigger from '../../../shared/modal/ModalTrigger';

type HourSelectProps = {
  questionTitle: string;
  questionTitleSuffix?: string;
  skipText?: string;
  modalText?: string;
  onSetValue: (value: { id: string; value: string }) => void;
  onOpenModal?: () => void;
  minuteSuffix: string;
  hourSuffix: string;
  questionid: string;
  assessmentValues: Record<string, string>;
  width: string;
};

export function HourSelect({
  questionTitle,
  questionTitleSuffix,
  skipText,
  modalText,
  onSetValue,
  onOpenModal,
  minuteSuffix,
  hourSuffix: hourSuffixFromProps,
  questionid,
  assessmentValues,
  width,
}: HourSelectProps) {
  const { t } = useTranslation();
  const [hourValue, setHourValue] = useState(0);
  const [minuteValue, setMinuteValue] = useState(0);
  const [hourSuffix, setHourSuffix] = useState(hourSuffixFromProps);
  const [skipActive, setSkipActive] = useState('');

  const setIncreaseClockHours = () => {
    const newHourValue = Number(hourValue) + 1;
    if (newHourValue < 24) {
      setHourValue(newHourValue);
      saveValue(`${newHourValue}:${minuteValue}`);
    }

    if (newHourValue === 1) {
      setHourSuffix(t('A121'));
    } else {
      setHourSuffix(t('A122'));
    }
  };

  const setDecreaseClockHours = () => {
    let newHourValue = Number(hourValue);
    if (Number(hourValue) > 0) {
      newHourValue = Number(hourValue) - 1;
    }
    setHourValue(newHourValue);
    saveValue(`${newHourValue}:${minuteValue}`);

    if (newHourValue === 1) {
      setHourSuffix(t('A121'));
    } else {
      setHourSuffix(t('A122'));
    }
  };

  const setIncreaseClockMinutes = () => {
    let newMinuteValue = Number(minuteValue);
    if (Number(minuteValue) < 45) {
      newMinuteValue = Number(minuteValue) + 15;
    } else if (questionid === 'hours_sitting') {
      newMinuteValue = 0;
    } else {
      newMinuteValue = 0;
    }
    setMinuteValue(newMinuteValue);
    saveValue(`${hourValue}:${newMinuteValue}`);
  };

  const setDecreaseClockMinutes = () => {
    let newMinuteValue = Number(minuteValue);
    if (Number(minuteValue) > 0) {
      newMinuteValue = Number(minuteValue) - 15;
      if (newMinuteValue <= 0) {
        newMinuteValue = 0;
      }
    } else {
      newMinuteValue = 45;
    }
    setMinuteValue(newMinuteValue);
    saveValue(`${hourValue}:${newMinuteValue}`);
  };

  const toggleSkipActive = () => {
    if (skipActive === 'active') {
      setSkipActive('');
      onSetValue({ id: questionid, value: '' });
    } else {
      setSkipActive('active');
      onSetValue({ id: questionid, value: 'wheelchair' });
    }
  };

  const clockChangeHandler = (event) => {
    if (event.target.id === 'hours') {
      setHourValue(event.target.value);
      saveValue(`${event.target.value}:${minuteValue}`);
    } else if (event.target.id === 'minutes') {
      setMinuteValue(event.target.value);
      saveValue(`${hourValue}:${event.target.value}`);
    }
  };

  const handleFocusOut = (event) => {
    if (event.target.id === 'hours') {
      let hours = Number(event.target.value);
      if (hours > 23) {
        hours = 23;
      } else if (hours < 0) {
        hours = 0;
      } else if (isNaN(hours)) {
        hours = 0;
      }
      if (hours === 1) {
        setHourSuffix(t('A121'));
      } else {
        setHourSuffix(t('A122'));
      }
      setHourValue(hours);
      saveValue(`${hours}:${minuteValue}`);
    } else if (event.target.id === 'minutes') {
      let mins = Number(event.target.value);
      if (mins > 45) {
        mins = 45;
      } else if (mins < 0) {
        mins = 0;
      } else if (isNaN(mins)) {
        mins = 0;
      }
      setMinuteValue(mins);
      saveValue(`${hourValue}:${mins}`);
    }
  };

  const saveValue = (time) => {
    onSetValue({ id: questionid, value: time });
  };

  useEffect(() => {
    if (assessmentValues[questionid]) {
      const time = assessmentValues[questionid];
      if (time === 'wheelchair') {
        setSkipActive('active');
      } else {
        const index = time.indexOf(':');
        let hour = time.substr(0, index);
        let minute = time.substr(index + 1);
        if (!Number(hour)) {
          hour = '00';
        }

        if (!Number(minute)) {
          minute = '00';
        }
        setHourValue(Number(hour));
        setMinuteValue(Number(minute));
      }
    } else if (questionid === 'hours_sitting') {
      saveValue('0:0');
    } else {
      saveValue('00:00');
    }
  }, []);

  return (
    <div className={`question_block${width} hour_select_container`}>
      <fieldset id={questionid} data-input-type="hour-select">
        <legend className="assessment_question_title">{questionTitle}</legend>
        {modalText && (
          <ModalTrigger
            modalText={modalText}
            onOpenModal={onOpenModal}
            question={questionid}
            questionTitle={questionTitle}
          />
        )}

        {questionTitleSuffix ? (
          <span className="titleSuffix fs-exclude">{questionTitleSuffix}</span>
        ) : null}
        {skipText ? (
          <button
            type="button"
            className={`suffixLabel suffixLabel_hours fs-exclude ${skipActive}`}
            onClick={toggleSkipActive}
          >
            {skipText}
          </button>
        ) : null}
        <div className="answer_block">
          {skipActive !== 'active' && (
            <div className="number_field hour_select">
              <div className="number_container left fs-exclude">
                <input
                  type="number"
                  className="hours number"
                  id="hours"
                  value={hourValue}
                  onChange={clockChangeHandler}
                  onBlur={handleFocusOut}
                  max="23"
                  min="00"
                />
                <label htmlFor="hours" className="suffix">
                  {hourSuffix}
                </label>
              </div>
              <div className="number_controls">
                <button
                  type="button"
                  className="up_btn"
                  onClick={setIncreaseClockHours}
                  aria-labelledby={`${questionid}_hours_up`}
                >
                  <span className="visuallyHidden" id={`${questionid}_hours_up`} aria-hidden="true">
                    {t('A134')} {t('A132')}
                  </span>
                  <FontAwesomeIcon icon={faChevronUp} className="icon" />
                </button>
                <button
                  type="button"
                  className="down_btn"
                  onClick={setDecreaseClockHours}
                  aria-labelledby={`${questionid}_hours_down`}
                >
                  <span
                    className="visuallyHidden"
                    id={`${questionid}_hours_down`}
                    aria-hidden="true"
                  >
                    {t('A135')} {t('A132')}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} className="icon" />
                </button>
              </div>
              <div className="number_container right fs-exclude">
                <input
                  type="number"
                  className="minutes number"
                  id="minutes"
                  value={minuteValue}
                  onChange={clockChangeHandler}
                  onBlur={handleFocusOut}
                  step="15"
                  max="45"
                  min="00"
                />
                <label htmlFor="minutes" className="suffix">
                  {minuteSuffix}
                </label>
              </div>
              <div className="number_controls">
                <button
                  type="button"
                  className="up_btn"
                  onClick={setIncreaseClockMinutes}
                  aria-labelledby={`${questionid}_minutes_up`}
                >
                  <span
                    className="visuallyHidden"
                    id={`${questionid}_minutes_up`}
                    aria-hidden="true"
                  >
                    {t('A134')} {t('A131')}
                  </span>
                  <FontAwesomeIcon icon={faChevronUp} className="icon" />
                </button>
                <button
                  type="button"
                  className="down_btn"
                  onClick={setDecreaseClockMinutes}
                  aria-labelledby={`${questionid}_minutes_down`}
                >
                  <span
                    className="visuallyHidden"
                    id={`${questionid}_minutes_down`}
                    aria-hidden="true"
                  >
                    {t('A135')} {t('A131')}
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} className="icon" />
                </button>
              </div>
            </div>
          )}
        </div>
      </fieldset>
    </div>
  );
}
